import {
  BarChart3,
  Brain,
  User,
  Briefcase,
  Palette,
  Truck,
  Building,
  Scale,
  DollarSign,
  TrendingUp,
} from 'lucide-react';

export interface ModuleConfig {
  name: string;
  icon: React.ComponentType<any>;
  path?: string;
  category: string;
  children?: Array<{ name: string; path: string }>;
}

// Mapping between database modules and UI configuration
export const MODULE_MAPPING: Record<string, ModuleConfig> = {
  // Dashboard
  'Dashboard': {
    name: 'Dashboard',
    icon: BarChart3,
    path: '/dashboard',
    category: 'core'
  },
  
  // Administration - Core admin functionality
  'Administration': {
    name: 'Administration',
    icon: User,
    path: '/admin',
    category: 'admin'
  },
  
  // Documents (formerly "Knowledge")
  'Documents': {
    name: 'Documents',
    icon: Brain,
    path: '/dashboard/knowledge',
    category: 'core'
  },
  
};

// Category configuration for grouping modules
export const CATEGORY_CONFIG = {
  'core': { name: 'Core', icon: BarChart3 },
  'sales': { name: 'Sales', icon: Briefcase },
  'design': { name: 'Design', icon: Palette },
  'field': { name: 'Field', icon: Truck },
  'vendor': { name: 'Vendor', icon: Building },
  'codes': { name: 'Codes', icon: Scale },
  'finance': { name: 'Finance', icon: DollarSign },
  'growth': { name: 'Growth', icon: TrendingUp }
};

export const getModuleConfig = (moduleName: string): ModuleConfig | null => {
  // Direct match first
  if (MODULE_MAPPING[moduleName]) {
    return MODULE_MAPPING[moduleName];
  }
  
  // Fallback: try flexible matching for common variations
  const normalizedName = moduleName.replace(/\s+/g, ' ').trim();
  if (MODULE_MAPPING[normalizedName]) {
    return MODULE_MAPPING[normalizedName];
  }
  
  // Try with hyphens instead of spaces
  const hyphenatedName = normalizedName.replace(/\s+/g, '-');
  if (MODULE_MAPPING[hyphenatedName]) {
    return MODULE_MAPPING[hyphenatedName];
  }
  
  // Try with spaces instead of hyphens
  const spacedName = normalizedName.replace(/-/g, ' ');
  if (MODULE_MAPPING[spacedName]) {
    return MODULE_MAPPING[spacedName];
  }
  
  return null;
};

/**
 * Helper function to get route path for a module
 * Uses MODULE_MAPPING for consistent path resolution
 */
export const getPathForModule = (moduleName: string): string | undefined => {
  // Try direct lookup in MODULE_MAPPING
  const moduleConfig = MODULE_MAPPING[moduleName];
  if (moduleConfig?.path) {
    return moduleConfig.path;
  }

  // Fallback: use getModuleConfig for flexible matching
  const config = getModuleConfig(moduleName);
  return config?.path;
};

/**
 * Helper function to get route path for admin modules
 * Admin modules have special path prefixes
 */
export const getPathForAdminModule = (moduleName: string): string | undefined => {
  const adminPathMap: { [key: string]: string } = {
    'User Management': '/dashboard/admin/users',
    'Knowledge Base': '/dashboard/admin/knowledge',
    'My Company': '/dashboard/admin/company',
    // backend admin modules
    'Upload Documents': '/dashboard/admin/upload',
    'Cloud Import': '/dashboard/admin/cloud-import',
    'File Library': '/dashboard/admin/knowledge-base',
    'Entity Types': '/dashboard/admin/entity-types',
    'Review Queue': '/dashboard/admin/review-queue',
    'Entity QA': '/dashboard/admin/entity-qa',
  };
  return adminPathMap[moduleName];
};

// Map section keys to module names for lookup in moduleMapping
// Used by mobile navigation and general layout
export const SECTION_TO_MODULE: Record<string, string> = {
  'knowledge': 'Documents',
};

// Map admin section keys to admin module names
export const ADMIN_SECTION_TO_MODULE: Record<string, string> = {
  'admin': 'User Management',
  'admin/users': 'User Management',
  'admin/knowledge': 'Knowledge Base',
  'admin/knowledge-base': 'File Library',
  'admin/company': 'My Company',
  'admin/upload': 'Upload Documents',
  'admin/cloud-import': 'Cloud Import',
  'admin/review-queue': 'Review Queue',
  'admin/entity-types': 'Entity Types',
  'admin/entity-qa': 'Entity QA',
  'dashboard/admin': 'User Management',
  'dashboard/admin/users': 'User Management',
  'dashboard/admin/knowledge': 'Knowledge Base',
  'dashboard/admin/company': 'My Company',
  'dashboard/admin/upload': 'Upload Documents',
  'dashboard/admin/cloud-import': 'Cloud Import',
  'dashboard/admin/knowledge-base': 'File Library',
  'dashboard/admin/entity-types': 'Entity Types',
  'dashboard/admin/review-queue': 'Review Queue',
  'dashboard/admin/entity-qa': 'Entity QA',
};

/**
 * Get route for a section using existing moduleMapping
 * Checks admin modules first, then regular modules
 */
export const getRouteForSection = (section: string): string => {
  // Check admin modules first
  const adminModuleName = ADMIN_SECTION_TO_MODULE[section];
  if (adminModuleName) {
    const adminPath = getPathForAdminModule(adminModuleName);
    if (adminPath) return adminPath;
  }

  // Check regular modules
  const moduleName = SECTION_TO_MODULE[section];
  if (moduleName) {
    const path = getPathForModule(moduleName);
    if (path) return path;
  }

  // Fallback: convert section to path format
  return `/${section}`;
};